import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Download, ChevronLeft, ChevronRight, Loader2, Users, Zap, Plus, ArrowUpRight, Archive, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { STATUS_META, StatusBadge, formatDate, CHARGER_LABELS, CHARGER_VALUES, formatValue } from "./statusConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["all", "new", "contacted", "nurture", "converted", "lost"];

export default function LeadsDashboard() {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(data);
    } catch {
      toast.error("Couldn't load stats");
    }
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        params: { page, limit: 15, search: debouncedSearch || undefined, status: statusFilter },
        withCredentials: true,
      });
      setLeads(data.leads);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      toast.error("Couldn't load leads");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { setPage(1); setSelected([]); }, [debouncedSearch, statusFilter]);

  const handleExport = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lk-leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported to CSV");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleBulkAction = async (action) => {
    const label = action === "archive" ? "archive" : "delete";
    if (!window.confirm(`Are you sure you want to ${label} ${selected.length} lead(s)?`)) return;
    try {
      await axios.post(`${API}/admin/leads/bulk-${action}`, { lead_ids: selected }, { withCredentials: true });
      toast.success(`${selected.length} lead(s) ${label}d`);
      setSelected([]);
      fetchLeads();
      fetchStats();
    } catch (err) {
      toast.error(`Couldn't ${label} leads`, { description: err.response?.data?.detail || "Please try again." });
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === leads.length) {
      setSelected([]);
    } else {
      setSelected(leads.map((l) => l.id));
    }
  };

  const statCards = stats ? [
    { key: "total", label: "Total Leads", value: stats.total, color: "#FFFFFF" },
    { key: "new", label: "New", value: stats.new, color: STATUS_META.new.color },
    { key: "contacted", label: "Contacted", value: stats.contacted, color: STATUS_META.contacted.color },
    { key: "nurture", label: "Nurture", value: stats.nurture, color: STATUS_META.nurture.color },
    { key: "converted", label: "Converted", value: stats.converted, color: STATUS_META.converted.color },
    { key: "lost", label: "Lost", value: stats.lost, color: STATUS_META.lost.color },
  ] : [];

  const openPipelineValue = stats?.open_value ?? 0;
  const convertedValue = stats?.converted_value ?? 0;

  return (
    <div data-testid="leads-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Lead Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">Every quote request from your landing page, in one place.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-2.5 px-4 rounded-lg transition-colors text-sm"
            data-testid="add-lead-button"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-[#27272a] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
            data-testid="export-csv-button"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Pipeline value strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="value-strip">
          <div className="bg-[#18181b] rounded-xl p-4 border border-[#27272a]">
            <p className="text-gray-500 text-xs">Open Pipeline</p>
            <p className="text-2xl font-bold text-[#F5C518] mt-1" data-testid="open-pipeline-value">{formatValue(openPipelineValue)}</p>
          </div>
          <div className="bg-[#18181b] rounded-xl p-4 border border-[#27272a]">
            <p className="text-gray-500 text-xs">Converted Value</p>
            <p className="text-2xl font-bold text-[#34D399] mt-1" data-testid="converted-value">{formatValue(convertedValue)}</p>
          </div>
          <div className="bg-[#18181b] rounded-xl p-4 border border-[#27272a]">
            <p className="text-gray-500 text-xs">Conversion Rate</p>
            <p className="text-2xl font-bold text-white mt-1" data-testid="conversion-rate">{stats.conversion_rate}%</p>
          </div>
          <div className="bg-[#18181b] rounded-xl p-4 border border-[#27272a]">
            <p className="text-gray-500 text-xs">Total Value</p>
            <p className="text-2xl font-bold text-white mt-1" data-testid="total-value">{formatValue(stats.total_value)}</p>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="stats-grid">
        {statCards.map((card) => (
          <button
            key={card.key}
            onClick={() => setStatusFilter(card.key === "total" ? "all" : card.key)}
            className={`bg-[#18181b] rounded-xl p-4 border transition-all text-left ${
              statusFilter === card.key || (card.key === "total" && statusFilter === "all")
                ? "border-[#F5C518]/60"
                : "border-[#27272a] hover:border-[#3f3f46]"
            }`}
            data-testid={`stat-card-${card.key}`}
          >
            <p className="text-2xl md:text-3xl font-bold" style={{ color: card.color }}>{card.value}</p>
            <p className="text-gray-500 text-xs mt-1">{card.label}</p>
          </button>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, email or eircode..."
            className="bg-[#18181b] border border-[#27272a] text-white rounded-lg pl-10 pr-4 py-2.5 w-full focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none transition-all text-sm"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="status-filters">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3.5 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                statusFilter === s
                  ? "bg-[#F5C518] text-black"
                  : "bg-[#18181b] text-gray-400 border border-[#27272a] hover:text-white"
              }`}
              data-testid={`filter-${s}`}
            >
              {s === "all" ? "All" : STATUS_META[s].label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-4 bg-[#18181b] rounded-xl border border-[#F5C518]/30" data-testid="bulk-action-bar">
          <span className="text-sm text-gray-300">{selected.length} selected</span>
          <button
            onClick={() => handleBulkAction("archive")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30 hover:bg-orange-500/25 transition-colors"
            data-testid="bulk-archive-button"
          >
            <Archive className="w-3.5 h-3.5" />
            Archive Selected
          </button>
          <button
            onClick={() => handleBulkAction("delete")}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 transition-colors"
            data-testid="bulk-delete-button"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete Selected
          </button>
          <button
            onClick={() => setSelected([])}
            className="text-xs text-gray-500 hover:text-white ml-auto"
            data-testid="bulk-clear-selection"
          >
            Clear
          </button>
        </div>
      )}

      {/* Leads table */}
      <div className="bg-[#18181b] rounded-xl border border-[#27272a] overflow-hidden" data-testid="leads-table-container">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 text-[#F5C518] animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 font-medium">No leads found</p>
            <p className="text-gray-600 text-sm mt-1">
              {debouncedSearch || statusFilter !== "all" ? "Try a different search or filter." : "New quote requests will appear here automatically."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#27272a] text-left">
                  <th className="px-4 py-3.5 text-gray-500 font-medium w-8">
                    <input
                      type="checkbox"
                      checked={selected.length === leads.length && leads.length > 0}
                      onChange={(e) => setSelected(e.target.checked ? leads.map((l) => l.id) : [])}
                      className="w-4 h-4 rounded border-[#3f3f46] bg-[#27272a] text-[#F5C518] focus:ring-2 focus:ring-[#F5C518] accent-[#F5C518]"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-5 py-3.5 text-gray-500 font-medium">Name</th>
                  <th className="px-5 py-3.5 text-gray-500 font-medium hidden md:table-cell">Contact</th>
                  <th className="px-5 py-3.5 text-gray-500 font-medium hidden lg:table-cell">Charger</th>
                  <th className="px-5 py-3.5 text-gray-500 font-medium">Status</th>
                  <th className="px-5 py-3.5 text-gray-500 font-medium hidden sm:table-cell">Received</th>
                  <th className="px-5 py-3.5 text-gray-500 font-medium text-right">View</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} className="border-b border-[#27272a]/50 hover:bg-white/[0.02] transition-colors" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-4 py-4 w-8">
                      <input
                        type="checkbox"
                        checked={selected.includes(lead.id)}
                        onChange={(e) => setSelected((s) => e.target.checked ? [...s, lead.id] : s.filter((id) => id !== lead.id))}
                        className="w-4 h-4 rounded border-[#3f3f46] bg-[#27272a] text-[#F5C518] focus:ring-2 focus:ring-[#F5C518] accent-[#F5C518]"
                        data-testid={`select-lead-${lead.id}`}
                      />
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-white font-medium">{lead.name}</p>
                      <p className="text-gray-500 text-xs md:hidden mt-0.5">{lead.phone}</p>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <p className="text-gray-300">{lead.phone}</p>
                      <p className="text-gray-500 text-xs">{lead.email}</p>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className="inline-flex items-center gap-1.5 text-gray-300">
                        <Zap className="w-3.5 h-3.5 text-[#F5C518]" />
                        {CHARGER_LABELS[lead.charger_preference] || lead.charger_preference}
                      </span>
                      {lead.lead_value != null && (
                        <p className="text-gray-500 text-xs mt-0.5">{formatValue(lead.lead_value)}</p>
                      )}
                    </td>
                    <td className="px-5 py-4"><StatusBadge status={lead.status || "new"} /></td>
                    <td className="px-5 py-4 text-gray-400 hidden sm:table-cell">{formatDate(lead.created_at)}</td>
                    <td className="px-5 py-4 text-right">
                      <Link
                        to={`/admin/leads/${lead.id}`}
                        className="inline-flex items-center gap-1 text-[#F5C518] hover:text-[#FFD54F] font-medium text-sm transition-colors"
                        data-testid={`view-lead-${lead.id}`}
                      >
                        Open
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-[#27272a]">
            <p className="text-gray-500 text-sm">{total} leads total</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-gray-400 text-sm px-2" data-testid="page-indicator">Page {page} of {pages}</span>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page === pages}
                className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      {showAddModal && <AddLeadModal onClose={() => setShowAddModal(false)} onCreated={() => { setShowAddModal(false); fetchLeads(); fetchStats(); }} />}
    </div>
  );
}

function AddLeadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    eircode: "",
    charger_preference: "unsure",
    prewired: "no",
    whatsapp_consent: true,
    lead_value: "",
    status: "new",
    flags: [],
  });

  const FLAG_OPTIONS = [
    { key: "site_survey", label: "Needs Site Survey" },
    { key: "fuseboard_upgrade", label: "Fuseboard Upgrade" },
    { key: "cable_extension", label: "Cable Extension" },
    { key: "solar", label: "Solar Interest" },
  ];

  const toggleFlag = (key) => {
    setForm((f) => ({ ...f, flags: f.flags.includes(key) ? f.flags.filter((x) => x !== key) : [...f.flags, key] }));
  };
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/leads`, {
        ...form,
        lead_value: form.lead_value ? parseInt(form.lead_value, 10) : null,
      }, { withCredentials: true });
      toast.success("Lead added");
      onCreated();
    } catch (err) {
      toast.error("Couldn't add lead", { description: err.response?.data?.detail || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="add-lead-modal">
      <div className="bg-[#18181b] rounded-2xl border border-[#27272a] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-white mb-1">Add Lead Manually</h2>
        <p className="text-gray-500 text-sm mb-5">Add a lead from a phone call or walk-in enquiry.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Name *</label>
              <input type="text" name="name" required value={form.name} onChange={handleChange} className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none" data-testid="add-lead-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Phone *</label>
              <input type="tel" name="phone" required value={form.phone} onChange={handleChange} className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none" data-testid="add-lead-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
              <input type="email" name="email" required value={form.email} onChange={handleChange} className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none" data-testid="add-lead-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Eircode</label>
              <input type="text" name="eircode" value={form.eircode} onChange={handleChange} className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none uppercase" data-testid="add-lead-eircode" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Charger</label>
              <select name="charger_preference" value={form.charger_preference} onChange={handleChange} className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none" data-testid="add-lead-charger">
                <option value="unsure">Not sure yet</option>
                <option value="bg-sync">BG Sync EV — €1,295 (€995 after grant)</option>
                <option value="zappi-glo">MyEnergi Zappi GLO — €1,495 (€1,195 after grant)</option>
                <option value="zappi">MyEnergi Zappi V2.1 — €1,695 (€1,395 after grant)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Pre-wired</label>
              <select name="prewired" value={form.prewired} onChange={handleChange} className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none" data-testid="add-lead-prewired">
                <option value="no">No / Not sure</option>
                <option value="yes">Yes, pre-wired</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Lead Value (€)</label>
              <input type="number" name="lead_value" min="0" value={form.lead_value} onChange={handleChange} placeholder="Auto from charger" className="bg-[#0f0f10] border border-[#27272a] text-white rounded-lg p-3 w-full text-sm focus:ring-2 focus:ring-[#F5C518] focus:border-transparent outline-none" data-testid="add-lead-value" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="whatsapp_consent" checked={form.whatsapp_consent} onChange={handleChange} className="w-5 h-5 rounded border-[#3f3f46] bg-[#27272a] text-[#F5C518] focus:ring-2 focus:ring-[#F5C518] accent-[#F5C518]" data-testid="add-lead-consent" />
                <span className="text-sm text-gray-400">WhatsApp OK</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Flags (optional)</label>
            <div className="flex flex-wrap gap-2">
              {FLAG_OPTIONS.map((opt) => (
                <label key={opt.key} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                  form.flags.includes(opt.key)
                    ? "bg-[#F5C518]/15 text-[#F5C518] border-[#F5C518]/40"
                    : "bg-white/5 text-gray-400 border-[#27272a] hover:border-[#3f3f46]"
                }`}>
                  <input type="checkbox" checked={form.flags.includes(opt.key)} onChange={() => toggleFlag(opt.key)} className="hidden" />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="flex-1 bg-[#F5C518] hover:bg-[#FFD54F] text-black font-bold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="add-lead-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Lead
            </button>
            <button type="button" onClick={onClose} className="px-5 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-white bg-white/5 transition-colors">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
